import {
  useContext,
  useEffect,
  useState,
} from 'react';

import {
  Box, Grid, Typography,
} from '@mui/material';

import { ImagesContext } from '../../../../contexts/ImagesContext';

// Components
import LimiterInput from './LimiterInput';

// Enums
import { InputLimits } from '../../../../enums/InputLimits';

interface Props {
  title?: string
  description?: string
  keywords?: string
  faviconUrl?: string
  faviconFile?: Blob
  onTitleChange?: (title: string) => void
  onDescriptionChange?: (description: string) => void
  onKeywordsChange?: (keywords: string) => void
  onFaviconFileChange?: (favicon_file: Blob) => void
}

function BasicSetting(
  props: Props,
) {
  const {
    title,
    description,
    keywords,
    faviconUrl,
    faviconFile,
    onTitleChange,
    onDescriptionChange,
    onKeywordsChange,
    onFaviconFileChange,
  } = props;
  const { exchangeForUrl } = useContext(ImagesContext);

  // initial values
  const [titleValue, setTitleValue] = useState<string>(title ?? '');
  const [descriptionValue, setDescriptionValue] = useState<string>(description ?? '');
  const [keywordsValue, setKeywordsValue] = useState<string>(keywords ?? '');

  const [imagePreviewUrl, setImagePreviewUrl] = useState<string>(faviconUrl ?? '/img/icon_gray_64.png');

  useEffect(() => {
    // check if the faviconUrl is a blob url
    if (faviconFile != null) {
      const reader = new FileReader();
      reader.onloadend = () => setImagePreviewUrl(reader.result as string);
      reader.readAsDataURL(faviconFile);

      return;
    }

    if (faviconUrl == null) return;

    exchangeForUrl(faviconUrl).then((url) => {
      setImagePreviewUrl(url);
    });
  }, [exchangeForUrl, faviconFile, faviconUrl]);

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    e.preventDefault();

    const reader = new FileReader();
    const file = e.target.files?.[0];
    const fileReaderForBinary = new FileReader();
    fileReaderForBinary.onload = () => {
      const res = fileReaderForBinary.result as ArrayBuffer;
      const blobData = new Blob([res]);
      onFaviconFileChange(blobData);
    };
    fileReaderForBinary.readAsArrayBuffer(file);

    reader.onloadend = () => {
      setImagePreviewUrl(reader.result as string);
    };

    if (file) {
      reader.readAsDataURL(file);
    }
  };

  return (
    <Box display="flex" flexDirection="column" rowGap="10px">
      <Box>
        <h2 className="mb-2">基本設定</h2>
      </Box>
      <Box className="pb-2">
        <hr className="mt-4" />
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <LimiterInput
              required
              value={titleValue}
              limit={InputLimits.TITLE_LIMIT}
              placeholder="ページのタイトル"
              label="ページのタイトル(title)"
              onChange={(newTitle: string) => {
                setTitleValue(newTitle);
                if (onTitleChange) {
                  onTitleChange(newTitle);
                }
              }}
            />

            <Typography variant="caption">
              ※ページのタイトル(title)はGoogleなどの検索エンジンの検索結果で表示されるテキストとなります。
            </Typography>
          </Grid>
        </Grid>
        <hr className="mt-4" />
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <LimiterInput
              textarea
              value={descriptionValue}
              limit={InputLimits.DESCRIPTION_LIMIT}
              placeholder="ページの説明文"
              label="ページの説明文(description)"
              onChange={(newDescription: string) => {
                setDescriptionValue(newDescription);
                if (onDescriptionChange) {
                  onDescriptionChange(newDescription);
                }
              }}
            />

            <Typography variant="caption">
              ※該当ページの説明文・要約となります。ページタイトルとともにGoogleなどの検索エンジンでの検索結果で表示される部分になります。
            </Typography>
          </Grid>
        </Grid>
        <hr className="mt-4" />
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <LimiterInput
              textarea
              value={keywordsValue}
              limit={InputLimits.KEYWORDS_LIMIT}
              placeholder="ページのキーワード"
              label="ページのキーワード(keywords)"
              onChange={(newKeywords: string) => {
                setKeywordsValue(newKeywords);
                if (onKeywordsChange) {
                  onKeywordsChange(newKeywords);
                }
              }}
            />

            <Typography variant="caption">
              ※該当ページの内容を示すワードとなります。
            </Typography>
          </Grid>
        </Grid>
        <hr className="mt-4" />
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Typography variant="body2" className="text-bold mb-2">
              ファビコンの設定
            </Typography>
            <Box className="setting-image-favicon mb-2">
              <img src={imagePreviewUrl} alt="favicon" />
            </Box>
            <input
              type="file"
              id="upload-button"
              className="form-control mb-1"
              style={{ display: 'none' }}
              onChange={handleImageChange}
            />
            <label htmlFor="upload-button" className="btn btn-primary mb-2">
              画像をアップロードする
            </label>
            <br />
            <Typography variant="caption">
              ※ブラウザのタブやお気に入り（favorite）やブックマークに入れた時などに表示されるWebサイトのアイコン画像のになります。
            </Typography>
            <br />
            <Typography variant="caption">
              ※最大アップロードサイズ：5MBまで、またファイル形式：png形式でアップロード下さい。
            </Typography>
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
}

export default BasicSetting;
